import { Directive, ElementRef, Inject, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { QuillEditorComponent } from 'ngx-quill';
import { Subscription } from 'rxjs';
import { DOCUMENT } from '@angular/common';

/**
 * Ensures the Quill editor surface exposes proper ARIA semantics.
 */
@Directive({
  selector: 'quill-editor[appQuillA11y]',
  standalone: true
})
export class QuillA11yDirective implements OnInit, OnDestroy, OnChanges {
  @Input('appQuillA11y') ariaLabel?: string | null;
  @Input() quillAriaLabelledby?: string | null;
  @Input() quillAriaDescribedby?: string | null;

  private editorReadySub?: Subscription;
  private toolbarObserver?: MutationObserver;
  private static stylesInjected = false;

  constructor(
    private readonly quillEditorComponent: QuillEditorComponent,
    private readonly host: ElementRef<HTMLElement>,
    @Inject(DOCUMENT) private readonly doc: Document
  ) { }

  ngOnInit(): void {
    this.ensureToolbarStyles();
    this.editorReadySub = this.quillEditorComponent.onEditorCreated.subscribe(() => this.applyAttributes());
  }

  ngOnChanges(): void {
    this.applyAttributes();
  }

  ngOnDestroy(): void {
    this.editorReadySub?.unsubscribe();
    this.disconnectToolbarObserver();
  }

  private applyAttributes(): void {
    const editor = this.quillEditorComponent.quillEditor?.root as HTMLElement | undefined;
    if (!editor) {
      return;
    }

    editor.setAttribute('role', 'textbox');
    editor.setAttribute('aria-multiline', 'true');

    this.setOrRemoveAttribute(editor, 'aria-label', this.ariaLabel);
    this.setOrRemoveAttribute(editor, 'aria-labelledby', this.quillAriaLabelledby);
    this.setOrRemoveAttribute(editor, 'aria-describedby', this.quillAriaDescribedby);

    this.applyToolbarAccessibility();
  }

  private setOrRemoveAttribute(element: HTMLElement, attribute: string, value?: string | null): void {
    if (value) {
      element.setAttribute(attribute, value);
    } else {
      element.removeAttribute(attribute);
    }
  }

  private applyToolbarAccessibility(): void {
    const toolbar = this.host.nativeElement.querySelector<HTMLElement>('.ql-toolbar');
    if (!toolbar) {
      this.disconnectToolbarObserver();
      return;
    }

    this.decorateToolbar(toolbar);

    if (!this.toolbarObserver) {
      this.toolbarObserver = new MutationObserver(() => this.decorateToolbar(toolbar));
      this.toolbarObserver.observe(toolbar, { attributes: true, childList: true, subtree: true });
    }
  }

  private decorateToolbar(toolbar: HTMLElement): void {
    this.decorateFormatButtons(toolbar);
    this.decoratePicker(toolbar, 'ql-header', 'Text style', value => this.describeHeader(value));
    this.decoratePicker(toolbar, 'ql-align', 'Text align', value => this.describeAlign(value));
    this.decorateColorPicker(toolbar, 'ql-color', 'Text color');
    this.decorateColorPicker(toolbar, 'ql-background', 'Text highlight color');
  }

  private decorateFormatButtons(toolbar: HTMLElement): void {
    const buttonLabels: Record<string, string> = {
      bold: 'Toggle bold text',
      italic: 'Toggle italic text',
      underline: 'Toggle underline',
      strike: 'Toggle strikethrough',
      clean: 'Remove formatting',
      link: 'Insert link',
      image: 'Insert image',
      video: 'Insert video'
    };

    Object.entries(buttonLabels).forEach(([cls, label]) => {
      toolbar.querySelectorAll<HTMLButtonElement>(`button.ql-${cls}`).forEach(button => {
        this.setAriaLabel(button, label);
      });
    });
  }

  private decoratePicker(
    toolbar: HTMLElement,
    pickerClass: string,
    labelPrefix: string,
    describeValue: (value: string | null) => string
  ): void {
    toolbar.querySelectorAll<HTMLElement>(`.ql-picker.${pickerClass}`).forEach(picker => {
      const labelEl = picker.querySelector<HTMLElement>('.ql-picker-label');
      const currentValue = labelEl?.getAttribute('data-value') ?? null;
      if (labelEl) {
        this.setAriaLabel(labelEl, `${labelPrefix}: ${describeValue(currentValue)}`);
      }
      picker.querySelectorAll<HTMLElement>('.ql-picker-item').forEach(option => {
        const optionValue = option.getAttribute('data-value') ?? null;
        this.setAriaLabel(option, `${labelPrefix} option: ${describeValue(optionValue)}`);
      });
    });
  }

  private decorateColorPicker(toolbar: HTMLElement, pickerClass: string, labelPrefix: string): void {
    toolbar.querySelectorAll<HTMLElement>(`.${pickerClass}`).forEach(picker => {
      const labelEl = picker.querySelector<HTMLElement>('.ql-picker-label');
      const currentValue = labelEl?.getAttribute('data-value') ?? null;
      if (labelEl) {
        this.setAriaLabel(labelEl, `${labelPrefix}: ${this.describeColor(currentValue)}`);
      }
      picker.querySelectorAll<HTMLElement>('.ql-picker-item').forEach(option => {
        const optionValue = option.getAttribute('data-value') ?? null;
        this.setAriaLabel(option, `${labelPrefix} option: ${this.describeColor(optionValue)}`);
      });
    });
  }

  private describeHeader(value: string | null): string {
    const map: Record<string, string> = {
      '1': 'Heading 1',
      '2': 'Heading 2',
      '3': 'Heading 3',
      '4': 'Heading 4',
      '5': 'Heading 5',
      '6': 'Heading 6',
      false: 'Normal text'
    };
    return map[value ?? ''] || 'Normal text';
  }

  private describeAlign(value: string | null): string {
    const map: Record<string, string> = {
      '': 'Left',
      left: 'Left',
      center: 'Center',
      right: 'Right',
      justify: 'Justify'
    };
    return map[value ?? ''] || 'Left';
  }

  private describeColor(value: string | null): string {
    if (!value || value === 'false') {
      return 'default';
    }
    return value.toUpperCase();
  }

  private disconnectToolbarObserver(): void {
    this.toolbarObserver?.disconnect();
    this.toolbarObserver = undefined;
  }

  private setAriaLabel(element: Element, value: string): void {
    if (element.getAttribute('aria-label') !== value) {
      element.setAttribute('aria-label', value);
    }
  }

  private ensureToolbarStyles(): void {
    if (QuillA11yDirective.stylesInjected || !this.doc?.head) {
      return;
    }
    const style = this.doc.createElement('style');
    style.setAttribute('data-ql-a11y', 'true');
    style.textContent = `
.ql-toolbar.ql-snow button,
.ql-toolbar.ql-snow .ql-picker-label,
.ql-toolbar.ql-snow .ql-picker-item {
  border: 1px solid transparent;
  border-radius: 4px;
}

.ql-toolbar.ql-snow button.ql-active,
.ql-toolbar.ql-snow .ql-picker-label.ql-active,
.ql-toolbar.ql-snow .ql-picker-item.ql-selected {
  border-color: #0d47a1;
  box-shadow: inset 0 0 0 1px #0d47a1;
  background-color: #e6f0ff;
}

.ql-toolbar.ql-snow button:focus-visible,
.ql-toolbar.ql-snow .ql-picker-label:focus-visible,
.ql-toolbar.ql-snow .ql-picker-item:focus-visible {
  outline: 2px solid #212121;
  outline-offset: 1px;
  box-shadow: none;
}

.ql-toolbar.ql-snow .ql-color .ql-picker-item:focus-visible,
.ql-toolbar.ql-snow .ql-background .ql-picker-item:focus-visible,
.ql-toolbar.ql-snow .ql-color .ql-picker-options .ql-picker-item:focus-visible,
.ql-toolbar.ql-snow .ql-background .ql-picker-options .ql-picker-item:focus-visible {
  outline: 1px solid #fff;
  box-shadow: 0 0 0 2px #000;
  border-radius: 2px;
}
    `;
    this.doc.head.appendChild(style);
    QuillA11yDirective.stylesInjected = true;
  }
}
